import React from 'react';
import { graphql } from 'gatsby';
import styled from 'styled-components';
import Title from '@/components/Title';

import { mediaQuery } from '@/utils/helpers';
import RichText, { RichTextType } from './Richtext';
import Link, { LinkStyle, LinkType } from './Link';
import Badge, { BadgeType } from './Badge';
import Image, { ImageType } from '@/components/Image';

type CTAImageContentProps = {
  direction: string;
};
const CTAImageContent = styled.li<CTAImageContentProps>`
  display: flex;
  &:not(:last-child) {
    margin: 0 0 4.8rem 0;
  }
  padding: 0;
  flex-direction: column;
  ${mediaQuery.md(`
  margin: 0;
`)};
  ${({ direction }) => {
    return mediaQuery.md(`
    padding: 2.4rem;
    gap: 8rem;
    flex-direction: ${direction};`);
  }};
`;

const CTAWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 1rem;
`;
const BlockImage = styled(Image)`
  border-radius: 24px;
`;
const BlockContent = styled.div`
  flex-basis: 100%;

  ${mediaQuery.md(`
  flex-basis: 50%;
`)};
  ${mediaQuery.lg(`
  margin-top: 2.4rem;
`)};
`;

const ImageContent = styled.div`
  margin-bottom: 2.4rem;
  flex-basis: 100%;
  ${mediaQuery.md(`
  flex-basis: 50%;
`)};
`;

export type Props = CTAImageLeftOrRightData;

export type CTAImageLeftOrRightData = RichTextType & {
  title: string;
  badge?: BadgeType;
  primaryLink?: LinkType;
  secondaryLink?: LinkType;
  image: ImageType;
  left: boolean;
  dark: boolean;
};

const CTAImageLeftOrRight: React.FC<Props> = ({
  title,
  badge,
  richText,
  primaryLink,
  secondaryLink,
  image,
  left,
  dark,
  defaultPaths,
}) => {
  const options: any = {
    theme: dark ? 'dark' : 'white',
    marginBottom: '2rem',
  };
  const direction: string = left ? 'row' : 'row-reverse';
  return (
    <CTAImageContent direction={direction}>
      <ImageContent>
        <BlockImage {...image} />
      </ImageContent>
      <BlockContent>
        {badge && <Badge {...badge} margin='0 0 1rem' />}

        <Title size='lg' level='h3' margin='0 0 2rem' lightColor={dark}>
          {title}
        </Title>

        {richText && (
          <RichText
            data={richText}
            defaultPaths={defaultPaths}
            options={options}
            isInternalLinkButton
          />
        )}
        <CTAWrapper>
          <Link {...primaryLink} linkStyle={LinkStyle.PRIMARY} />
          <Link {...secondaryLink} linkStyle={LinkStyle.SECONDARY} />
        </CTAWrapper>
      </BlockContent>
    </CTAImageContent>
  );
};

export default CTAImageLeftOrRight;

export const ctaImageLeftOrRightFragmentQuery = graphql`
  fragment CTAImageLeftOrRightFragment on ContentfulCtaImageLeftOrRight {
    badge {
      ...BadgeFragment
    }
    image {
      gatsbyImageData(width: 1104)
      ...ImageFragment
    }
    title
    richText {
      ...RichTextFragment
    }
    primaryLink {
      ...LinkFragment
    }
    secondaryLink {
      ...LinkFragment
    }
  }
`;
